/**
 * Cupcake Company — Weekly Flavor Schedule (single source of truth)
 * -------------------------------------------------------------------
 * Both the Daily Menu section and the Box Builder (order-wizard) read
 * from this file, so updates stay in sync automatically. Edit flavor
 * lists HERE — both surfaces pick up the change on the next page load.
 *
 * Structure:
 *   - everyday:  6 flavors that are available every day the shop is open
 *   - daily:     day-specific flavors (8 per day) for Tuesday–Saturday
 *   - gfPerDay:  exactly 2 gluten-friendly flavors per day (badge only —
 *                they still come from the everyday/daily pool above)
 *
 * Per-day total = 6 everyday + 8 daily + 2 GF = 16 flavors
 *
 * Flavor names are written in canonical Title Case and must match the
 * titles of Shopify products in the `cupcake-flavors` collection so the
 * wizard can look them up. Consumers that match against Shopify product
 * titles should lowercase at the comparison site — helpers below do this.
 *
 * Exposed globally as `window.CupcakeSchedule`.
 */
(function () {
  'use strict';

  /* ── EVERYDAY FLAVORS (6) ─────────────────────────────────── */
  var everyday = [
    'Vanilla Vanilla',
    'Double Chocolate',
    'Red Velvet',
    'Cookies & Cream',
    'Chocolate Vanilla',
    'Vanilla Chocolate'
  ];

  /* ── DAILY FLAVORS (8 per day, Tuesday–Saturday) ──────────── */
  var daily = {
    tuesday: [
      'Butterfinger',
      'Carrot',
      'Chocolate Chip Cookie Dough',
      'Dreamsicle',
      'Raspberry Lemon',
      'Strawberry Crunch',
      'Vanilla Caramel Latte',
      'Wet Coconut'
    ],
    wednesday: [
      'Caramel Banana Nut',
      'Cherry Cheesecake',
      'Chocolate Ganache',
      'Chocolate Mint',
      'Chocolate Strawberry',
      'Key Lime',
      'Lemon Cheesecake',
      'Pineapple Upside Down'
    ],
    thursday: [
      'Chocolate Cheesecake',
      'Chocolate Salted Caramel',
      'Dreamsicle',
      'Hummingbird',
      'Lemon Drop',
      'Peanut Butter Cookie',
      'Pina Colada',
      'Strawberry Cheesecake'
    ],
    friday: [
      'Blueberry Lemon',
      'Carrot',
      'Cherry Cheesecake',
      'Chocolate Strawberry',
      'German Chocolate',
      'Raspberry Merlot',
      'Salted Caramel',
      'Strawberry Crunch'
    ],
    saturday: [
      'Banana Bread Cheesecake',
      'Chocolate Chip Cookie Dough',
      'Dreamsicle',
      'Pancake Maple Bacon',
      'Peanut Butter Crunch',
      'Raspberry Lemon',
      'Strawberry Cheesecake',
      'Vanilla Strawberry'
    ]
  };

  /* ── GLUTEN-FRIENDLY FLAVORS PER DAY (exactly 2) ──────────────
     These are DISTINCT products from their regular-named counterparts.
     Each has its own SKU in Square (e.g., CUP-REDVLVGF is a different
     SKU from CUP-REDVLV). The wizard renders them as separate tiles
     so every day shows exactly 16 options: 6 Everyday + 8 Daily + 2 GF.
     Naming matches Square product titles for 1:1 mapping. */
  var gfPerDay = {
    tuesday:   ['Chocolate Vanilla Gluten Friendly', 'Raspberry Lemon Gluten Friendly'],
    wednesday: ['Double Chocolate Gluten Friendly', 'Lemon Cheesecake Gluten Friendly'],
    thursday:  ['Hummingbird Gluten Friendly', 'Lemon Drop Gluten Friendly'],
    friday:    ['Carrot Gluten Friendly', 'Swiss Roll Gluten Friendly'],
    saturday:  ['Chocolate Vanilla Gluten Friendly', 'Red Velvet Gluten Friendly']
  };

  /* ── SUMMER MENU (active for pickup dates from 2026-06-02 onward) ──
     Source: Notion "Cupcake Flavor Change Instructions" 2026-05-14.
     Doc shape: each weekday lists 5 "Every <weekday>" flavors
     (year-round daily anchor) + 5 "Summer <weekday>" flavors
     (seasonal rotation) = 10 distinct per day. Combined with the 6
     `everyday` flavors above the customer sees 16 options per day.

     5 new SKUs introduced (already in Shopify, no images yet — the
     wizard renders them with the no-image placeholder):
       CUP-CTNCAN  Cotton Candy        (Summer Tue + Summer Fri)
       CUP-NUDBUD  Nuddy Buddy         (Summer Tue)
       CUP-SMORE   S'mores             (Summer Wed + Summer Fri)
       CUP-MNTCC   Mint Cookies & Cream(Summer Thu)
       CUP-BERCC   Berry Cheesecake    (Summer Thu)
     The 4 *(GF)* variants new to summer (Vanilla GF, PB Cookie GF,
     Chocolate Cheesecake GF, Blueberry Lemon GF) are added below as
     `syntheticFlavors` so the wizard can render them as tiles even
     before Shopify products exist for them. */
  var summerDaily = {
    tuesday: [
      /* Every Tuesday — year-round daily anchor */
      'Wet Coconut',
      'Chocolate Chip Cookie Dough',
      'Vanilla Caramel Latte',
      'Carrot',
      'Butterfinger',
      /* Summer Tuesday */
      'Cotton Candy',
      'Nuddy Buddy',
      'Strawberry Crunch'
      /* GF variants (Carrot GF, Double Chocolate GF) live in summerGfPerDay
         only — listing them here too double-rendered them as plain tiles. */
    ],
    wednesday: [
      /* Every Wednesday */
      'Lemon Cheesecake',
      'Chocolate Mint',
      'Cherry Cheesecake',
      'Chocolate Ganache',
      'Caramel Banana Nut',
      /* Summer Wednesday */
      "S'mores",
      'Dreamsicle',
      'Key Lime'
      /* GF variants (Red Velvet GF, Vanilla GF) live in summerGfPerDay only. */
    ],
    thursday: [
      /* Every Thursday */
      'Hummingbird',
      'Chocolate Cheesecake',
      'Chocolate Salted Caramel',
      'Vanilla Strawberry',
      'Peanut Butter Cookie',
      /* Summer Thursday */
      'Mint Cookies & Cream',
      'Berry Cheesecake',
      'Pineapple Upside Down'
      /* GF variants (PB Cookie GF, Chocolate Cheesecake GF) live in summerGfPerDay only. */
    ],
    friday: [
      /* Every Friday — 'Chocolate Chip Cookie Dough' removed from Friday per
         Ryan/Chris 2026-06-04 (it was listed here in error). It remains a
         Tuesday + Saturday flavor; the product itself is untouched — this is
         purely the Friday day-visibility list for the Daily Menu + Box Builder. */
      'German Chocolate',
      'Raspberry Merlot',
      'Carrot',
      'Cherry Cheesecake',
      'Salted Caramel',
      /* Summer Friday */
      'Strawberry Crunch',
      "S'mores",
      'Cotton Candy'
      /* GF variants (Swiss Roll GF, Carrot GF) live in summerGfPerDay only. */
    ],
    saturday: [
      /* Every Saturday */
      'Banana Bread Cheesecake',
      'Pancake Maple Bacon',
      'Peanut Butter Crunch',
      'Chocolate Chip Cookie Dough',
      'Vanilla Strawberry',
      /* Summer Saturday */
      'Dreamsicle',
      'Chocolate Strawberry',
      'Blueberry Lemon'
      /* GF variants (Red Velvet GF, Blueberry Lemon GF) live in summerGfPerDay only. */
    ]
  };

  /* GF tags for SUMMER — the *(GF)* annotations in the Notion doc.
     These flavors render with a GF badge in the wizard for that day. */
  var summerGfPerDay = {
    tuesday:   ['Carrot Gluten Friendly', 'Double Chocolate Gluten Friendly'],
    wednesday: ['Red Velvet Gluten Friendly', 'Vanilla Gluten Friendly'],
    thursday:  ['Peanut Butter Cookie Gluten Friendly', 'Chocolate Cheesecake Gluten Friendly'],
    friday:    ['Swiss Roll Gluten Friendly', 'Carrot Gluten Friendly'],
    saturday:  ['Red Velvet Gluten Friendly', 'Blueberry Lemon Gluten Friendly']
  };

  /* ── SYNTHETIC FLAVORS ────────────────────────────────────────
     Flavor names that the box builder treats as available even if
     the product isn't yet published in the `cupcake-flavors`
     Shopify collection. The Daily Menu uses this same list so its
     "exists in the store" filter matches the Box Builder's.

     Includes:
       - Regular flavors currently missing from Shopify
         (Caramel Banana Nut, Pina Colada)
       - All Gluten-Friendly variants (Square has these as distinct
         SKUs like CUP-REDVLVGF; Shopify doesn't have separate products
         yet, so the wizard needs these synthetic entries to render
         them as dedicated tiles)
       - All new SUMMER 2026 flavors (CUP-CTNCAN, CUP-NUDBUD, CUP-SMORE,
         CUP-MNTCC, CUP-BERCC) plus their accompanying new GF variants. */
  var syntheticFlavors = [
    /* Regular flavors */
    'Vanilla Vanilla',
    'Vanilla Chocolate',
    'Chocolate Vanilla',
    'Strawberry Cheesecake',
    'Strawberry Crunch',
    'Vanilla Caramel Latte',
    'Wet Coconut',
    'Vanilla Strawberry',
    'Caramel Banana Nut',
    'Pina Colada',
    /* New SUMMER 2026 flavors (Notion doc 2026-05-14, new SKUs already
       exist in Shopify but have no images yet — wizard renders the
       no-image placeholder until photos are uploaded). */
    'Cotton Candy',
    'Nuddy Buddy',
    "S'mores",
    'Mint Cookies & Cream',
    'Berry Cheesecake',
    /* Gluten-Friendly variants (distinct products) */
    'Carrot Gluten Friendly',
    'Swiss Roll Gluten Friendly',
    'Chocolate Vanilla Gluten Friendly',
    'Double Chocolate Gluten Friendly',
    'Hummingbird Gluten Friendly',
    'Lemon Cheesecake Gluten Friendly',
    'Lemon Drop Gluten Friendly',
    'Raspberry Lemon Gluten Friendly',
    'Red Velvet Gluten Friendly',
    /* New SUMMER GF variants (no Shopify products yet). */
    'Vanilla Gluten Friendly',
    'Peanut Butter Cookie Gluten Friendly',
    'Chocolate Cheesecake Gluten Friendly',
    'Blueberry Lemon Gluten Friendly'
  ];

  /* ── SEASONS — date-driven selection ──────────────────────────
     The box builder reads from the season that matches the customer's
     CHOSEN pickup/delivery date (not the current date). A customer
     ordering on May 14 for pickup on June 5 will see the summer menu.

     The boundary table is processed in order: the FIRST entry whose
     `start` date is ≤ the picker date wins. New seasons can be added
     by extending this table; no other code change required.

     The bare `daily` / `gfPerDay` objects above stay as the spring
     config so the Daily Cupcake Menu page (which reads them directly)
     keeps its current behavior. Per Mike 2026-05-14: "EDITS TO BOX
     BUILDER ONLY. CHANGE NOTHING ELSE." */
  var seasons = {
    spring: { daily: daily,       gfPerDay: gfPerDay },
    summer: { daily: summerDaily, gfPerDay: summerGfPerDay }
  };

  /* Earliest start dates per season. Order doesn't matter; lookup
     picks the season whose start is the latest one ≤ the target date. */
  var seasonBoundaries = [
    { start: '2026-01-01', season: 'spring' },
    { start: '2026-06-02', season: 'summer' }  /* "after June 1" per Notion doc */
  ].sort(function (a, b) { return a.start.localeCompare(b.start); });

  function getSeasonForDate(date) {
    if (!date) return 'spring';  /* No date → fall back to the storefront default */
    var d = date instanceof Date ? date : new Date(date);
    if (isNaN(d)) return 'spring';
    /* Compare YYYY-MM-DD to dodge TZ off-by-one issues. */
    var ymd = d.getFullYear() + '-' +
              String(d.getMonth() + 1).padStart(2, '0') + '-' +
              String(d.getDate()).padStart(2, '0');
    var chosen = 'spring';
    for (var i = 0; i < seasonBoundaries.length; i++) {
      if (ymd >= seasonBoundaries[i].start) chosen = seasonBoundaries[i].season;
    }
    return chosen;
  }

  /* ── HELPERS ──────────────────────────────────────────────── */

  /**
   * Lowercase + strip diacritics so "Piña Colada" and "Pina Colada"
   * compare equal. Used on both sides of every flavor-name match so
   * a Shopify product titled with ASCII-only characters still maps
   * to its schedule entry (and vice versa).
   */
  function normalize(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Strip trailing "(cupcake)" / "(cupcakes)" product-title suffixes so
      // schedule names like "Cherry Cheesecake" match Shopify titles like
      // "Cherry Cheesecake (Cupcakes)" without us having to rename the
      // Shopify product (renaming pushes back to Square via DPL).
      .replace(/\s*\((cupcake|cupcakes)\)\s*$/i, '')
      .trim();
  }

  /**
   * Returns an array of every flavor name (normalized) that can appear
   * on any day — the union of `everyday`, all `daily[day]` lists, AND
   * all `gfPerDay[day]` lists. GF items must be in the whitelist so
   * GF-only flavors (like Chocolate Vanilla on Tue/Sat, Chocolate Swiss
   * Roll on Fri) render in the wizard even when they're not in the
   * regular daily pool.
   * Used by the box builder as its approved-flavors whitelist.
   */
  function allFlavorNamesLower() {
    var set = {};
    everyday.forEach(function (n) { set[normalize(n)] = true; });
    /* Union across ALL seasons so the wizard whitelist accepts every
       flavor that could possibly appear, regardless of which season is
       active at init time. Without this, summer-only flavors (Cotton
       Candy, S'mores, etc.) would be filtered out before the date is
       even chosen. */
    Object.keys(seasons).forEach(function (seasonKey) {
      var s = seasons[seasonKey];
      Object.keys(s.daily).forEach(function (day) {
        s.daily[day].forEach(function (n) { set[normalize(n)] = true; });
      });
      Object.keys(s.gfPerDay).forEach(function (day) {
        s.gfPerDay[day].forEach(function (n) { set[normalize(n)] = true; });
      });
    });
    return Object.keys(set);
  }

  /**
   * True if `flavorName` (case-insensitive) is a gluten-friendly
   * flavor for the given `dayName` (e.g. 'tuesday', 'Friday') in the
   * season matching `date` (optional — defaults to spring for legacy
   * call sites without a date).
   */
  function isGlutenFriendlyOn(flavorName, dayName, date) {
    var key = String(dayName || '').toLowerCase();
    var season = getSeasonForDate(date);
    var list = seasons[season].gfPerDay[key];
    if (!list) return false;
    var target = String(flavorName || '').toLowerCase();
    return list.some(function (n) { return n.toLowerCase() === target; });
  }

  /**
   * For a given day, returns the flavors that should appear on the
   * Daily Menu / Box Builder for that day: the 6 everyday + the
   * day-specific list for the season matching `date`.
   *
   * - `date` (optional Date): if provided, resolves the season. Without
   *   it, falls back to the spring config (matches legacy callers, e.g.
   *   the Daily Menu page that doesn't pick a season).
   */
  function flavorsForDay(dayName, date) {
    var key = String(dayName || '').toLowerCase();
    var season = getSeasonForDate(date);
    var dayList = seasons[season].daily[key] || [];
    var gfList  = seasons[season].gfPerDay[key] || [];
    return {
      everyday: everyday.slice(),
      daily: dayList.slice(),
      gf: gfList.slice(),
      season: season
    };
  }

  window.CupcakeSchedule = {
    everyday: everyday,
    daily: daily,                    /* spring (back-compat for Daily Menu page) */
    gfPerDay: gfPerDay,              /* spring (back-compat for Daily Menu page) */
    seasons: seasons,                /* new — date-driven seasonal data */
    syntheticFlavors: syntheticFlavors,
    normalize: normalize,
    allFlavorNamesLower: allFlavorNamesLower,
    isGlutenFriendlyOn: isGlutenFriendlyOn,
    flavorsForDay: flavorsForDay,
    getSeasonForDate: getSeasonForDate
  };
})();
